import React, { useMemo, useRef, useState } from "react";
import { Row, Col, Form, Input, Button, Typography, Tag } from "antd";
import { SaveOutlined, ThunderboltOutlined, DashboardOutlined, StarFilled, StarOutlined } from "@ant-design/icons";
import "../App.css";
import "./SaveTraining.css";
import "./Profile.css";
import SyncStrava from "./SyncStrava";
import { useDialog } from "./DialogProvider";
import axios from "axios";

const { Title, Text } = Typography;

function Profile({
  username,
  otherUserInfo,
  isPremium = false,
  FTP,
  weight,
  setFTP,
  setWeight,
  setOtherUserInfo,
}) {
  const { alert: alertDlg, confirm } = useDialog();
  const fileInputRef = useRef(null);
  const [localFTP, setLocalFTP] = useState(FTP || 0);
  const [localWeight, setLocalWeight] = useState(weight || 0);
  const [stravaAccessCode, setStravaAccessCode] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isBgUploading, setIsBgUploading] = useState(false);
  const [prevBgUrl, setPrevBgUrl] = useState(null);
  const [prevBgPath, setPrevBgPath] = useState(null);
  const [hasPendingBgChange, setHasPendingBgChange] = useState(false);

  // Strava sync UI is implemented by <SyncStrava />

  const email = otherUserInfo?.email || "";
  const isDefaultBg = useMemo(() => {
    return !(otherUserInfo?.bgUrl || otherUserInfo?.bgUrlPath);
  }, [otherUserInfo?.bgUrl, otherUserInfo?.bgUrlPath]);

  const axiosInstance = axios.create({
    baseURL: "/api",
    headers: { "Content-Type": "application/json" },
  });
  axiosInstance.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem("token");
      if (token) config.headers["Authorization"] = `Bearer ${token}`;
      return config;
    },
    (error) => Promise.reject(error)
  );
  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      if (error.response && error.response.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;
        try {
          const refreshToken = localStorage.getItem("refresh_token");
          const refreshResponse = await axios.post("/api/refresh_tokens/", { refresh_token: refreshToken });
          if (refreshResponse.status === 200) {
            const { access_token: newToken, refresh_token: newRefreshToken } = refreshResponse.data;
            localStorage.setItem("token", newToken);
            localStorage.setItem("refresh_token", newRefreshToken);
            originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
            return axiosInstance(originalRequest);
          }
        } catch (refreshError) {
          alertDlg("You’ve been inactive for too long. You might need to log in again.");
        }
      }
      return Promise.reject(error);
    }
  );

  const onUploadBg = async (file) => {
    try {
      if (!file) return;
      const maxBytes = 10 * 1024 * 1024;
      const allowed = ["image/png", "image/jpeg"];
      if (!allowed.includes(file.type)) {
        alertDlg("Only PNG and JPEG images are allowed.");
        return;
      }
      if (file.size > maxBytes) {
        alertDlg("Image is too large. Max size: 10MB.");
        return;
      }
      // keep previous values to allow discard
      setPrevBgUrl(otherUserInfo?.bgUrl || null);
      setPrevBgPath(otherUserInfo?.bgUrlPath || null);
      setIsBgUploading(true);
      const formData = new FormData();
      formData.append("file", file);
      const resp = await axiosInstance.post("/upload_bg/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = resp.data?.bg_url;
      const path = resp.data?.bg_url_path;
      if (url) {
        setOtherUserInfo({ ...otherUserInfo, bgUrl: url, bgUrlPath: path || otherUserInfo?.bgUrlPath || null });
        setHasPendingBgChange(true);
        alertDlg("Background updated. Tip: prefer light/white images for better readability.");
      } else {
        alertDlg("Upload succeeded but URL missing. Try refreshing.");
      }
    } catch (e) {
      alertDlg("Failed to upload background image.");
    } finally {
      setIsBgUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const onResetBg = async () => {
    try {
      if (!(await confirm("Restore default background?"))) return;
      await axiosInstance.post("/reset_bg/", {});
      setOtherUserInfo({ ...otherUserInfo, bgUrl: null, bgUrlPath: null });
      // clear any pending discard
      setPrevBgUrl(null);
      setPrevBgPath(null);
      alertDlg("Background restored to default.");
    } catch (e) {
      alertDlg("Failed to restore background.");
    }
  };
  
  const onDiscardBgChange = async () => {
    try {
      // If we have a path, persistently revert on backend; otherwise just revert locally
      if (prevBgPath) {
        const resp = await axiosInstance.post("/set_bg_path/", { bg_url_path: prevBgPath });
        const url = resp.data?.bg_url || prevBgUrl;
        setOtherUserInfo({ ...otherUserInfo, bgUrl: url, bgUrlPath: prevBgPath });
      } else if (prevBgUrl) {
        setOtherUserInfo({ ...otherUserInfo, bgUrl: prevBgUrl });
      } else {
        // No previous background (default) -> reset on server
        await axiosInstance.post("/reset_bg/", {});
        setOtherUserInfo({ ...otherUserInfo, bgUrl: null, bgUrlPath: null });
      }
      setPrevBgUrl(null);
      setPrevBgPath(null);
      setHasPendingBgChange(false);
      alertDlg("Background change discarded.");
    } catch (e) {
      alertDlg("Failed to discard changes.");
    }
  };

  const onSave = async () => {
    try {
      setIsSaving(true);
      const payload = {
        ftp: Number.isFinite(localFTP) && localFTP >= 0 ? parseInt(localFTP) : undefined,
        weight: Number.isFinite(localWeight) && localWeight > 0 ? parseInt(localWeight) : undefined,
      };
      const resp = await axiosInstance.post("/update_profile/", payload);
      const { ftp, weight: savedWeight } = resp.data || {};
      if (typeof ftp === "number") setFTP(ftp);
      if (typeof savedWeight === "number") setWeight(savedWeight);
      alertDlg("Profile saved.");
    } catch (e) {
      alertDlg("Failed to save profile. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="liquid-glass-card profile-card">
      <div className="profile-card__header">
        <Title level={4} style={{ margin: 0 }}>Profile</Title>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
          {isPremium ? (
            <Tag color="gold" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
              <StarFilled style={{ color: "#d48806" }} />
              Premium
            </Tag>
          ) : (
            <Tag color="default" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
              <StarOutlined />
              Free
            </Tag>
          )}
        </div>
      </div>
      <Form
        layout="vertical"
        className="glass-form"
        onFinish={onSave}
        initialValues={{
          username,
          email,
          weight: localWeight,
          ftp: localFTP,
        }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Username">
              <Input value={username || ""} disabled />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Email">
              <Input value={email || ""} disabled />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Weight"
              name="weight"
              help="Used to estimate your speed."
            >
              <Input
                type="number"
                addonAfter="kg"
                value={localWeight}
                onChange={(e) => {
                  const val = parseInt(e.target.value);
                  if (Number.isFinite(val) && val >= 0) setLocalWeight(val);
                  if (e.target.value === "") setLocalWeight(0);
                }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="FTP"
              name="ftp"
              help="Functional Threshold Power (set as default)."
            >
              <Input
                type="number"
                addonAfter="W"
                value={localFTP}
                onChange={(e) => {
                  const val = parseInt(e.target.value);
                  if (Number.isFinite(val) && val >= 0) setLocalFTP(val);
                  if (e.target.value === "") setLocalFTP(0);
                }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item style={{ marginBottom: 0 }} name="stravaAccessCode" label="">
              <SyncStrava
                axiosInstance={axiosInstance}
                otherUserInfo={otherUserInfo}
                setOtherUserInfo={setOtherUserInfo}
                code={stravaAccessCode}
                setCode={setStravaAccessCode}
                headerWidth="100%"
                headerBackground="#fd824e"
                contentBackground="var(--pf-surface)"
              />
            </Form.Item>
          </Col>
        </Row>
        
        <Row gutter={16} style={{ marginTop: 12 }}>
          <Col span={24}>
            <div className="glass-panel glass-section" style={{ padding: 12 }}>
              <Title level={5} style={{ margin: 0, marginBottom: 8 }}>Background image</Title>
              <div style={{ marginBottom: 8 }}>
                Prefer a light, mostly white image for best readability.
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg"
                style={{ display: "none" }}
                onChange={(e) => onUploadBg(e.target.files?.[0])}
              />
              <div style={{ display: "flex", gap: 8 }}>
                <Button
                  className="glass-cta"
                  type="default"
                  loading={isBgUploading}
                  onClick={() => fileInputRef.current && fileInputRef.current.click()}
                >
                  {isBgUploading ? "Uploading..." : "Change background"}
                </Button>
                {hasPendingBgChange && (
                  <Button
                    type="default"
                    onClick={onDiscardBgChange}
                  >
                    Discard
                  </Button>
                )}
                <Button
                  danger
                  type="default"
                  disabled={isDefaultBg}
                  onClick={onResetBg}
                >
                  Restore default
                </Button>
              </div>
            </div>
          </Col>
        </Row>

        <Row gutter={16} style={{ marginTop: 12 }}>
          <Col span={12}>
            <Button
              block
              className="primary-glass-btn"
              type="primary"
              htmlType="submit"
              loading={isSaving}
              icon={<SaveOutlined />}
            >
              Save
            </Button>
          </Col>
          <Col span={12}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, height: "100%", opacity: 0.85 }}>
              <DashboardOutlined />
              <Text>Current FTP: {FTP ?? "-" } W</Text>
              <ThunderboltOutlined />
              <Text>Current Weight: {weight ?? "-" } kg</Text>
            </div>
          </Col>
        </Row>
      </Form>
    </div>
  );
}

export default Profile;


